import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  FileText,
  Users,
  ArrowRight,
  FilePlus,
  Recycle,
  CalendarBlank,
} from '@phosphor-icons/react';
import { formatCurrency, formatDate, getStatusColor } from '../../lib/utils';

export const QuickActions = () => {
  const navigate = useNavigate();
  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          variant="outline"
          className="w-full justify-start h-12"
          onClick={() => navigate('/claims/new')}
          data-testid="quick-action-new-claim"
        >
          <FilePlus size={18} className="mr-3 text-indigo-600" />
          Create New Claim
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start h-12"
          onClick={() => navigate('/patients')}
          data-testid="quick-action-add-patient"
        >
          <Users size={18} className="mr-3 text-emerald-600" />
          Add Patient
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start h-12"
          onClick={() => navigate('/templates')}
          data-testid="quick-action-templates"
        >
          <Recycle size={18} className="mr-3 text-amber-600" />
          View Templates
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start h-12"
          onClick={() => navigate('/codes')}
          data-testid="quick-action-codes"
        >
          <CalendarBlank size={18} className="mr-3 text-blue-600" />
          HCPCS Codes
        </Button>
      </CardContent>
    </Card>
  );
};

const ClaimItem = ({ claim }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
        <FileText size={18} className="text-slate-600" />
      </div>
      <div>
        <p className="font-medium text-slate-900">{claim.patient_name}</p>
        <p className="text-sm text-slate-500">
          <span className="font-mono text-xs">{claim.service_code}</span>
          {' • '}
          {formatDate(claim.service_date)}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      <span className="font-mono text-sm font-medium text-slate-900">
        {formatCurrency(claim.estimated_reimbursement)}
      </span>
      <span className={`status-badge ${getStatusColor(claim.status)}`}>{claim.status}</span>
    </div>
  </div>
);

export const RecentClaimsCard = ({ recentClaims }) => {
  const navigate = useNavigate();
  return (
    <Card className="lg:col-span-2 border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
          Recent Claims
        </CardTitle>
        <Link
          to="/claims"
          className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          data-testid="view-all-claims-link"
        >
          View all <ArrowRight size={14} />
        </Link>
      </CardHeader>
      <CardContent>
        {recentClaims.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText size={40} className="mx-auto mb-3 text-slate-300" />
            <p>No claims yet</p>
            <Button
              variant="link"
              className="text-indigo-600 mt-2"
              onClick={() => navigate('/claims/new')}
            >
              Create your first claim
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {recentClaims.map((claim) => (
              <ClaimItem key={claim.id} claim={claim} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
